import { useEffect } from "react";
import { AppContainer } from "@/components/layout/app-container";
import {
  StatsCard,
  DashboardHero,
  DashboardPaymentsTable,
  BarChartCard,
  LineChartCard,
} from "@/components/dashboard";
import { Skeleton } from "@/components/ui/skeleton";
import { useProponenteDashboard } from "@/services/queries/useDashboard";
import { FileText, Wallet, ListChecks, Users, RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { IChartData, ICompactChartData } from "@/interfaces/dashboard.interface";

export default function ProponenteDashboard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useProponenteDashboard();

  // Manual refresh function
  const handleRefresh = () => {
    refetch();
  };

  // Auto-refresh is handled by React Query (configured in the hook)
  useEffect(() => {
    console.log("Proponente dashboard mounted - auto-refresh enabled");
  }, []);

  // if (error) {
  //   return (
  //     <AppContainer>
  //       <div className="flex items-center justify-center h-screen">
  //         <div className="text-center">
  //           <h2 className="text-xl font-bold text-white mb-2">Erro ao carregar dashboard</h2>
  //           <p className="text-gray-400 mb-4">{error instanceof Error ? error.message : 'Erro desconhecido'}</p>
  //           <button
  //             onClick={handleRefresh}
  //             className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
  //           >
  //             Tentar novamente
  //           </button>
  //         </div>
  //       </div>
  //     </AppContainer>
  //   );
  // }

  // Extract data with fallback to prevent errors during loading
  const hero = data?.hero || {
    userName: "Usuário",
    newOrders: 0,
    pendingItems: 0,
  };

  const stats = data?.stats || {
    contractsExpiring: 0,
    contractsPercentage: 0,
    budgetSpent: "R$0k",
    budgetPercentage: 0,
    tasksInProgress: 0,
    tasksPercentage: 0,
    activeUsers: 0,
    usersPercentage: 0,
  };

  const payments = data?.payments || [];
  const contractsChart = data?.contractsChart || [];
  const teamChart = data?.teamChart || [];
  const salesChart = data?.salesChart || [];
  const financeChart = data?.financeChart || [];
  return (
    <AppContainer
      title="Dashboard"
      button={
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      }
    >
      {/* Hero Section */}
      <DashboardHero
        userName={hero.userName}
        newOrders={hero.newOrders}
        pendingItems={hero.pendingItems}
      />

      {/* Two Column Layout - Slim Design */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 mt-6">
        {/* Left Column */}
        <div className="flex flex-col gap-6">
          {/* Stats Cards - Horizontal Layout */}
          {isLoading ? (
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[120px]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
              <StatsCard
                icon={FileText}
                label="Contratos Vencendo"
                value={stats.contractsExpiring}
                percentage={stats.contractsPercentage}
              />
              <StatsCard
                icon={Wallet}
                label="Orçamento gasto"
                value={stats.budgetSpent}
                percentage={stats.budgetPercentage}
              />
              <StatsCard
                icon={ListChecks}
                label="Tarefas em andamento"
                value={stats.tasksInProgress}
                percentage={stats.tasksPercentage}
              />
              <StatsCard
                icon={Users}
                label="Usuários ativos"
                value={stats.activeUsers}
                percentage={stats.usersPercentage}
              />
            </div>
          )}

          {/* Payments Table */}
          <DashboardPaymentsTable payments={payments} isLoading={isLoading} />
        </div>

        {/* Right Column - Compact Charts */}
        <div className="flex flex-col gap-4">
          <BarChartCard
            title="Contratos"
            data={contractsChart}
            percentage={10}
            period="Últimos 7 dias"
            isLoading={isLoading}
          />
          <BarChartCard
            title="Equipe"
            data={teamChart}
            percentage={10}
            period="Últimos 7 dias"
            isLoading={isLoading}
          />
          <LineChartCard
            title="Vendas"
            data={salesChart}
            percentage={5}
            period="Últimos 7 dias"
            isLoading={isLoading}
          />
          <LineChartCard
            title="Finanças"
            data={financeChart}
            percentage={5}
            period="Últimos 7 dias"
            isLoading={isLoading}
          />
        </div>
      </div>
    </AppContainer>
  );
}
